import React, { useState, useEffect, useRef } from 'react';
import { Save, X, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { lumi } from '../lib/lumi';
import toast from 'react-hot-toast';

interface EditableContentProps {
  contentKey: string;
  defaultContent: string;
  contentType?: 'text' | 'image' | 'html';
  className?: string;
  placeholder?: string;
  isEditMode: boolean;
  style?: React.CSSProperties;
  multiline?: boolean;
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
}

const EditableContent = React.memo<EditableContentProps>(({
  contentKey,
  defaultContent,
  contentType = 'text',
  className = '',
  placeholder = '내용을 입력하세요...',
  isEditMode,
  style = {},
  multiline = false,
  tag = 'div'
}) => {
  
  // 로컬 캐시 확인
  const getCachedData = () => {
    if (typeof window === 'undefined') return null;
    const cached = localStorage.getItem(`content_${contentKey}`);
    if (cached) {
      try { return JSON.parse(cached); } catch { return null; }
    }
    return null;
  };

  const cachedData = getCachedData();

  // 상태 관리
  const [content, setContent] = useState<string | null>(cachedData ? cachedData.content : null);
  const [editValue, setEditValue] = useState(content || defaultContent);
  const [isEditing, setIsEditing] = useState(false);
  
  // [디자인 수정] 캐시가 있으면 바로 보여주고(true), 없으면 페이드인 대기(false)
  const [isVisible, setIsVisible] = useState<boolean>(!!cachedData); 
  
  const [siteContent, setSiteContent] = useState<any>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 데이터 페칭
  useEffect(() => {
    let isMounted = true;
    const fetchContent = async () => {
      try {
        const { list: contents } = await lumi.entities.site_content.list({
          filter: { contentKey, published: true },
          limit: 1,
          sort: { updatedAt: -1 }
        });

        if (isMounted) {
          if (contents && contents.length > 0) {
            const serverData = contents[0];
            setSiteContent(serverData);

            if (!cachedData || serverData.content !== cachedData.content) {
              setContent(serverData.content);
              setEditValue(serverData.content);
              localStorage.setItem(`content_${contentKey}`, JSON.stringify({
                content: serverData.content,
                updatedAt: serverData.updatedAt,
                timestamp: Date.now()
              }));
            }
          } else {
            if (content === null) {
              setContent(defaultContent);
              setEditValue(defaultContent);
            }
          }
          // 데이터 로드 완료 시 부드럽게 나타나도록 설정
          setIsVisible(true);
        }
      } catch (error) {
        if (content === null) {
            setContent(defaultContent);
            setEditValue(defaultContent);
        }
        setIsVisible(true);
      }
    };
    setTimeout(fetchContent, 0);
    return () => { isMounted = false; };
  }, [contentKey, defaultContent]);

  // 저장 핸들러
  const handleSave = async () => {
    const toastId = toast.loading('저장 중...');
    try {
      let savedData;
      if (siteContent) {
        savedData = await lumi.entities.site_content.update(siteContent._id, {
          content: editValue,
          updatedAt: new Date().toISOString()
        });
      } else {
        savedData = await lumi.entities.site_content.create({
          contentKey,
          contentType,
          title: `${contentKey} 콘텐츠`,
          content: editValue,
          pageSection: contentKey.split('_')[0],
          order: 1,
          published: true,
          author: '관리자',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        setSiteContent(savedData);
      }
      setContent(editValue);
      localStorage.setItem(`content_${contentKey}`, JSON.stringify({
        content: editValue,
        updatedAt: new Date().toISOString(),
        timestamp: Date.now()
      }));
      setIsEditing(false);
      toast.success('저장되었습니다', { id: toastId });
    } catch (error) {
      toast.error('저장 실패', { id: toastId });
    }
  };

  const handleCancel = () => {
    setEditValue(content || defaultContent);
    setIsEditing(false);
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const loadingToast = toast.loading('업로드 중...');
    try {
      const fileArray = Array.from(files);
      const uploadResults = await lumi.tools.file.upload(fileArray);
      if (uploadResults[0].fileUrl) {
        setEditValue(uploadResults[0].fileUrl);
        toast.success('업로드 성공', { id: loadingToast });
      }
    } catch (e) {
      toast.error('업로드 실패', { id: loadingToast });
    }
  };

  // --- 렌더링 ---
  const Tag = tag as keyof JSX.IntrinsicElements;
  const displayContent = content || defaultContent;

  const formatText = (text: string) => {
    if (!text) return '';
    return text.split('\n').map((line, i) => (
      <React.Fragment key={i}>{line}{i < text.split('\n').length - 1 && <br />}</React.Fragment>
    ));
  };

  const renderContent = () => {
    // [디자인 수정] Fade-in 효과를 위한 클래스 추가
    // transition-opacity duration-700: 0.7초 동안 부드럽게 투명도 변화
    // isVisible ? 'opacity-100' : 'opacity-0': 로드 전엔 투명, 로드 후엔 보임
    const fadeClass = `transition-opacity duration-700 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'}`;

    if (contentType === 'image') {
        return (
          <img 
            src={displayContent} 
            alt="content" 
            className={`${className} ${fadeClass}`} 
            style={{...style, minHeight: isVisible ? 'auto' : '100px'}} // 로딩 중 최소 높이 확보
            loading="lazy" 
            onError={(e:any) => {
              if (defaultContent && defaultContent !== displayContent) e.target.src = defaultContent;
              else e.target.style.display = 'none'; 
            }} 
          />
        );
    }
    if (contentType === 'html') {
        return <Tag dangerouslySetInnerHTML={{ __html: displayContent }} className={`${className} ${fadeClass}`} style={style} />;
    }
    return <Tag className={`${className} ${fadeClass}`} style={style}>{formatText(displayContent)}</Tag>;
  };

  if (!isEditMode) return renderContent();

  return (
    <div className="relative group inline-block w-full">
      {!isEditing ? (
        <div 
            onClick={() => setIsEditing(true)} 
            className="relative border-2 border-dashed border-transparent hover:border-blue-300 rounded cursor-pointer transition-all min-h-[1em]"
            title="클릭하여 수정"
        >
          {renderContent()}
          <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity rounded pointer-events-none flex items-center justify-center">
             {/* 호버 시 수정 버튼 표시 */}
          </div>
        </div>
      ) : (
        <div className="relative z-50 bg-white border-2 border-blue-500 rounded-xl p-4 shadow-2xl min-w-[300px]">
           <div className="flex justify-between items-center mb-3 pb-2 border-b">
             <span className="text-sm font-bold text-gray-700 flex items-center gap-2">
                {contentType === 'image' ? <ImageIcon size={14}/> : <Save size={14}/>} 
                {contentType === 'image' ? '이미지 변경' : '텍스트 수정'}
             </span>
             <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600"><X size={16}/></button>
           </div>

           {contentType === 'image' ? (
             <div className="space-y-3">
                {editValue && <img src={editValue} className="h-32 object-contain mx-auto border rounded bg-gray-50" />}
                <div 
                    className={`border-2 border-dashed rounded p-4 text-center cursor-pointer transition-colors ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`}
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
                    onDrop={(e) => { e.preventDefault(); setIsDragOver(false); handleImageUpload(e.dataTransfer.files); }}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <div className="text-sm text-gray-500 flex flex-col items-center gap-1">
                        <Upload size={20}/>
                        <span>이미지 업로드 (클릭/드래그)</span>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e.target.files)} />
                </div>
                <input type="text" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-full text-sm border rounded p-2" placeholder="또는 이미지 URL 입력" />
             </div>
           ) : contentType === 'html' ? (
             <textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-full h-48 text-sm border rounded p-2 font-mono" placeholder="HTML 코드를 입력하세요" />
           ) : (
             multiline ? 
                <textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-full h-32 text-sm border rounded p-2" placeholder={placeholder} autoFocus /> :
                <input type="text" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-full text-sm border rounded p-2" placeholder={placeholder} autoFocus />
           )}

           <div className="flex justify-end gap-2 mt-4">
             <button onClick={handleCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">취소</button>
             <button onClick={handleSave} className="px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded flex items-center gap-1">
                <Save size={14}/> 저장
             </button>
           </div>
        </div>
      )}
    </div>
  );
});

EditableContent.displayName = 'EditableContent';
export default EditableContent;